/**
 * Administración de Políticas y Visión
 * Panel para gestionar documentos de políticas corporativas
 */
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Table, Badge, Spinner, Modal, Tabs, Tab, Pagination } from 'react-bootstrap';
import axios from 'axios';
import './AdminPoliticas.css';

const API_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'development' ? 'http://localhost:4005' : '');

const AdminPoliticas = () => {
  const [politicas, setPoliticas] = useState([]);
  const [politicaActiva, setPoliticaActiva] = useState(null);
  const [estadisticas, setEstadisticas] = useState(null);
  const [firmas, setFirmas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('gestion');
  const [filtroLegajo, setFiltroLegajo] = useState('');
  
  // Paginación de firmas
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // 10 firmas por página

  // Formulario nueva política
  const [showFormulario, setShowFormulario] = useState(false);
  const [formData, setFormData] = useState({
    version: '',
    titulo: '',
    descripcion: '',
    obligatorio: true,
    archivo: null
  });

  useEffect(() => {
    cargarDatos();
  }, []);

  useEffect(() => {
    if (activeTab === 'firmas') {
      cargarFirmas();
    }
  }, [activeTab]);

  // Resetear página cuando cambia el filtro de búsqueda
  useEffect(() => {
    setCurrentPage(1);
  }, [filtroLegajo]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Cargar política activa
      const activaRes = await axios.get(`${API_URL}/api/politicas/version-activa`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (activaRes.data.success && activaRes.data.data) {
        setPoliticaActiva(activaRes.data.data);

        // Cargar estadísticas de la activa
        const statsRes = await axios.get(
          `${API_URL}/api/politicas/estadisticas/${activaRes.data.data.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (statsRes.data.success) {
          setEstadisticas(statsRes.data.data);
        }
      } else {
        setPoliticaActiva(null);
        setEstadisticas(null);
      }

      // Cargar todas las políticas
      const listRes = await axios.get(`${API_URL}/api/politicas/listar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (listRes.data.success) {
        setPoliticas(listRes.data.data || []);
      }

    } catch (err) {
      console.error('Error cargando datos:', err);
      setError('Error al cargar las políticas');
    } finally {
      setLoading(false);
    }
  };

  const cargarFirmas = async (politicaId = null) => {
    try {
      const token = localStorage.getItem('token');
      const url = politicaId 
        ? `${API_URL}/api/politicas/firmas?politicaId=${politicaId}`
        : `${API_URL}/api/politicas/firmas`;
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success) {
        setFirmas(res.data.data || []);
      }
    } catch (err) {
      console.error('Error cargando firmas:', err);
      setError('Error al cargar las firmas');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setFormData(prev => ({ ...prev, archivo: file }));
    } else {
      alert('Solo se permiten archivos PDF');
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.version || !formData.titulo || !formData.archivo) {
      alert('Complete todos los campos requeridos');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const form = new FormData();
      form.append('version', formData.version);
      form.append('titulo', formData.titulo);
      form.append('descripcion', formData.descripcion);
      form.append('obligatorio', formData.obligatorio);
      form.append('archivo', formData.archivo);

      await axios.post(`${API_URL}/api/politicas/crear`, form, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setSuccess('Política creada correctamente');
      setShowFormulario(false);
      setFormData({
        version: '',
        titulo: '',
        descripcion: '',
        obligatorio: true,
        archivo: null
      });
      
      // Recargar datos
      setTimeout(() => {
        cargarDatos();
        setSuccess(null);
      }, 2000);

    } catch (err) {
      console.error('Error creando política:', err);
      setError(err.response?.data?.error || 'Error al crear la política');
    } finally {
      setUploading(false);
    }
  };

  const handleActivar = async (id) => {
    if (!window.confirm('¿Activar esta versión? Todos los usuarios deberán firmar nuevamente.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/politicas/activar/${id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Versión activada correctamente');
      setTimeout(() => {
        cargarDatos();
        setSuccess(null);
      }, 2000);

    } catch (err) {
      console.error('Error activando política:', err);
      setError('Error al activar la política');
    }
  };

  const calcularProgreso = () => {
    if (!estadisticas) return 0;
    const total = (estadisticas.total_empleados || 0) + (estadisticas.total_admins || 0);
    const firmadas = (estadisticas.firmas_empleados || 0) + (estadisticas.firmas_admins || 0);
    return total > 0 ? Math.round((firmadas / total) * 100) : 0;
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Cargando...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="admin-politicas-container py-4">
      <Row className="mb-4">
        <Col>
          <h2>Gestión de Políticas y Visión</h2>
          <p className="text-muted">
            Administre las políticas corporativas y controle su aceptación
          </p>
        </Col>
        <Col xs="auto">
          <Button 
            variant="primary" 
            size="lg"
            onClick={() => setShowFormulario(true)}
          >
            + Subir Nueva Política
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4"
      >
        <Tab eventKey="gestion" title="Gestión de Políticas">
          {/* Política Activa */}
          {politicaActiva ? (
            <Row className="mb-4 mt-3">
          <Col lg={12}>
            <Card className="shadow-sm border-0">
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={6}>
                    <div className="d-flex align-items-center mb-3">
                      <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{width: '50px', height: '50px', fontSize: '24px'}}>
                        📋
                      </div>
                      <div className="ms-3">
                        <h4 className="mb-1">{politicaActiva.titulo}</h4>
                        <Badge bg={politicaActiva.obligatorio ? 'danger' : 'info'}>
                          {politicaActiva.obligatorio ? 'Firma Obligatoria' : 'Firma Opcional'}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="mb-2">
                      <span className="text-muted">Versión:</span> <strong>{politicaActiva.version}</strong>
                    </div>
                    <div className="mb-2">
                      <span className="text-muted">Archivo:</span> <code className="small">{politicaActiva.archivo_nombre}</code>
                    </div>
                    <div className="mb-2">
                      <span className="text-muted">Publicada:</span> {new Date(politicaActiva.fecha_publicacion).toLocaleDateString('es-AR')}
                    </div>
                    {politicaActiva.descripcion && (
                      <div className="mt-3 p-3 bg-light rounded">
                        <small className="text-muted">{politicaActiva.descripcion}</small>
                      </div>
                    )}
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <h2 className="display-3 text-success mb-0">{calcularProgreso()}%</h2>
                      <p className="text-muted mb-3">Completado</p>
                      <div className="text-start">
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Empleados:</span>
                          <strong>{estadisticas?.firmas_empleados || 0} / {estadisticas?.total_empleados || 0}</strong>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Administradores:</span>
                          <strong>{estadisticas?.firmas_admins || 0} / {estadisticas?.total_admins || 0}</strong>
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="d-grid gap-2">
                      <Button 
                        variant="primary"
                        size="lg"
                        href={`${API_URL}/api/politicas/documento/${politicaActiva.id}?token=${localStorage.getItem('token')}`}
                        target="_blank"
                      >
                        Ver Documento
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      ) : (
        <Alert variant="warning">
          <strong>No hay ninguna política activa</strong>
          <p className="mb-0">Suba una nueva política y actívela para comenzar.</p>
        </Alert>
      )}

      {/* Tabla de todas las políticas */}
      <Row className="mt-4">
        <Col>
          <Card className="shadow-sm">
            <Card.Header className="bg-secondary text-white">
              <h5 className="mb-0">Historial de Políticas</h5>
            </Card.Header>
            <Card.Body>
              {politicas.length > 0 ? (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Versión</th>
                      <th>Título</th>
                      <th>Archivo</th>
                      <th>Estado</th>
                      <th>Obligatoria</th>
                      <th>Fecha Publicación</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {politicas.map((pol) => (
                      <tr key={pol.id}>
                        <td>{pol.id}</td>
                        <td><code>{pol.version}</code></td>
                        <td>
                          <strong>{pol.titulo}</strong>
                          {pol.descripcion && (
                            <div className="text-muted small">{pol.descripcion}</div>
                          )}
                        </td>
                        <td className="small">{pol.archivo_nombre}</td>
                        <td>
                          <Badge bg={pol.activo ? 'success' : 'secondary'}>
                            {pol.activo ? 'Activa' : 'Inactiva'}
                          </Badge>
                        </td>
                        <td className="text-center">
                          {pol.obligatorio ? (
                            <Badge bg="danger">Obligatoria</Badge>
                          ) : (
                            <Badge bg="info">Opcional</Badge>
                          )}
                        </td>
                        <td className="small">
                          {pol.fecha_publicacion 
                            ? new Date(pol.fecha_publicacion).toLocaleDateString() 
                            : '-'}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              size="sm"
                              variant="outline-primary"
                              href={`${API_URL}/api/politicas/documento/${pol.id}?token=${localStorage.getItem('token')}`}
                              target="_blank"
                            >
                              Ver
                            </Button>
                            {!pol.activo && (
                              <Button
                                size="sm"
                                variant="success"
                                onClick={() => handleActivar(pol.id)}
                                title="Activar esta política"
                              >
                                Activar
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Alert variant="info" className="mb-0">
                  No hay políticas cargadas aún.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

          {/* Instrucciones */}
          <Row className="mt-4">
            <Col>
              <Card className="bg-light">
                <Card.Body>
                  <h6>Instrucciones:</h6>
                  <ol className="mb-0">
                    <li>Suba un nuevo documento PDF con el botón "Subir Nueva Política"</li>
                    <li>La política se creará como <strong>inactiva</strong></li>
                    <li>Solo puede haber <strong>una política activa</strong> a la vez</li>
                    <li>Cuando active una nueva versión, todos los usuarios deberán firmar nuevamente</li>
                    <li>Las firmas anteriores se conservan en el historial</li>
                  </ol>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        <Tab eventKey="firmas" title="Registro de Firmas">
          <Row className="mt-3">
            <Col>
              <Card className="shadow-sm">
                <Card.Header className="bg-info text-white d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Usuarios que Firmaron</h5>
                  <Badge bg="light" text="dark">{firmas.length} firmas</Badge>
                </Card.Header>
                <Card.Body>
                  {/* Filtro de búsqueda por legajo */}
                  <Row className="mb-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Buscar por Legajo/ID</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Ingrese legajo o ID de usuario..."
                          value={filtroLegajo}
                          onChange={(e) => setFiltroLegajo(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {(() => {
                    const firmasFiltradas = firmas.filter((firma) => {
                      if (!filtroLegajo.trim()) return true;
                      const legajoStr = firma.tipo_usuario === 'admin' 
                        ? String(firma.cd_usuario)
                        : String(firma.legajo);
                      return legajoStr.includes(filtroLegajo.trim());
                    });

                    if (firmas.length === 0) {
                      return (
                        <Alert variant="info" className="mb-0">
                          No hay firmas registradas aún.
                        </Alert>
                      );
                    }

                    if (firmasFiltradas.length === 0) {
                      return (
                        <Alert variant="warning" className="mb-0">
                          No se encontraron firmas con el legajo/ID: <strong>{filtroLegajo}</strong>
                        </Alert>
                      );
                    }

                    // Calcular paginación
                    const totalPages = Math.ceil(firmasFiltradas.length / itemsPerPage);
                    const indexOfLastItem = currentPage * itemsPerPage;
                    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
                    const currentItems = firmasFiltradas.slice(indexOfFirstItem, indexOfLastItem);

                    // Función para cambiar de página
                    const handlePageChange = (pageNumber) => {
                      setCurrentPage(pageNumber);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    };

                    return (
                      <>
                        {filtroLegajo.trim() && (
                          <Alert variant="info" className="mb-3">
                            Mostrando {firmasFiltradas.length} de {firmas.length} firmas
                          </Alert>
                        )}
                        
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <small className="text-muted">
                            Página {currentPage} de {totalPages} • Mostrando {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, firmasFiltradas.length)} de {firmasFiltradas.length} registros
                          </small>
                        </div>

                        <Table striped bordered hover responsive>
                          <thead>
                            <tr>
                              <th>Usuario</th>
                              <th>Tipo</th>
                              <th>Legajo/ID</th>
                              <th>Política</th>
                              <th>Versión</th>
                              <th>Fecha Firma</th>
                              <th>PDF Firmado</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentItems.map((firma) => (
                              <tr key={firma.id}>
                                <td><strong>{firma.nombre_usuario}</strong></td>
                                <td>
                                  <Badge bg={firma.tipo_usuario === 'admin' ? 'danger' : 'primary'}>
                                    {firma.tipo_usuario === 'admin' ? 'Admin' : 'Empleado'}
                                  </Badge>
                                </td>
                                <td className="small">
                                  {firma.tipo_usuario === 'admin' 
                                    ? `U-${firma.cd_usuario}` 
                                    : `L-${firma.legajo}`}
                                </td>
                                <td className="small">{firma.politica_titulo}</td>
                                <td><code>{firma.politica_version}</code></td>
                                <td className="small">
                                  {new Date(firma.fecha_firma).toLocaleString('es-AR')}
                                </td>
                                <td>
                                  {firma.pdf_firmado_ruta ? (
                                    <Button
                                      size="sm"
                                      variant="success"
                                      href={`${API_URL}/api/politicas/documento-firmado/${firma.id}?token=${localStorage.getItem('token')}`}
                                      target="_blank"
                                      title="Ver PDF con firma aplicada"
                                    >
                                      Descargar PDF
                                    </Button>
                                  ) : (
                                    <Badge bg="secondary">No generado</Badge>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>

                        {/* Paginación */}
                        {totalPages > 1 && (
                          <div className="d-flex justify-content-center mt-4">
                            <Pagination>
                              <Pagination.First 
                                onClick={() => handlePageChange(1)} 
                                disabled={currentPage === 1}
                              />
                              <Pagination.Prev 
                                onClick={() => handlePageChange(currentPage - 1)} 
                                disabled={currentPage === 1}
                              />
                              
                              {[...Array(totalPages)].map((_, index) => {
                                const pageNumber = index + 1;
                                // Mostrar solo páginas cercanas a la actual
                                if (
                                  pageNumber === 1 ||
                                  pageNumber === totalPages ||
                                  (pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2)
                                ) {
                                  return (
                                    <Pagination.Item
                                      key={pageNumber}
                                      active={pageNumber === currentPage}
                                      onClick={() => handlePageChange(pageNumber)}
                                    >
                                      {pageNumber}
                                    </Pagination.Item>
                                  );
                                } else if (
                                  pageNumber === currentPage - 3 ||
                                  pageNumber === currentPage + 3
                                ) {
                                  return <Pagination.Ellipsis key={pageNumber} disabled />;
                                }
                                return null;
                              })}
                              
                              <Pagination.Next 
                                onClick={() => handlePageChange(currentPage + 1)} 
                                disabled={currentPage === totalPages}
                              />
                              <Pagination.Last 
                                onClick={() => handlePageChange(totalPages)} 
                                disabled={currentPage === totalPages}
                              />
                            </Pagination>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>
      </Tabs>

      {/* Formulario Modal */}
      <Modal 
        show={showFormulario} 
        onHide={() => !uploading && setShowFormulario(false)}
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton={!uploading}>
          <Modal.Title>Subir Nueva Política</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Versión *</Form.Label>
              <Form.Control
                type="text"
                name="version"
                value={formData.version}
                onChange={handleInputChange}
                placeholder="Ej: 1.0, 2.0, 2026-v1"
                required
                disabled={uploading}
              />
              <Form.Text className="text-muted">
                Identificador único de la versión
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Título *</Form.Label>
              <Form.Control
                type="text"
                name="titulo"
                value={formData.titulo}
                onChange={handleInputChange}
                placeholder="Ej: Políticas y Visión de la Empresa 2026"
                required
                disabled={uploading}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="descripcion"
                value={formData.descripcion}
                onChange={handleInputChange}
                placeholder="Descripción o resumen del documento"
                disabled={uploading}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Archivo PDF *</Form.Label>
              <Form.Control
                type="file"
                accept="application/pdf"
                onChange={handleFileChange}
                required
                disabled={uploading}
              />
              <Form.Text className="text-muted">
                Solo archivos PDF. Máximo 10 MB.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="obligatorio"
                label="Firma obligatoria (bloquea el acceso hasta que todos firmen)"
                checked={formData.obligatorio}
                onChange={handleInputChange}
                disabled={uploading}
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button 
                variant="secondary" 
                onClick={() => setShowFormulario(false)}
                disabled={uploading}
              >
                Cancelar
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      className="me-2"
                    />
                    Subiendo...
                  </>
                ) : (
                  'Subir Política'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default AdminPoliticas;
